// ===================================================================
// KAIXO - Modal de intercambio de contactos por QR
// ===================================================================
// Dos modos en pestañas:
//   • Mi QR: muestra un QR generado para que otro lo escanee. Pollea
//     status cada 2s; si alguien lo escanea, muestra su perfil y
//     cierra el modal con éxito.
//   • Escanear: abre cámara, busca QR cada 250ms con jsQR. Cuando
//     detecta el código, hace POST /redeem y muestra confirmación.
// Tras intercambio exitoso, AMBOS usuarios quedan como contactos
// mutuos en sus agendas (lo gestiona el backend en una sola request).
// ===================================================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import jsQR from 'jsqr';
import QRCode from 'qrcode';
import api from '../../services/api';
import { Icons } from '../Icons/Icons';
import './ContactExchangeModal.css';

const SCAN_INTERVAL_MS = 250;
const POLL_INTERVAL_MS = 2000;

// i18n local — strings de la UI. Mismo patrón que CallManager/CameraModal.
const CEX_I18N = {
    español: {
        title: 'Intercambiar contacto',
        tabShare: 'Mi QR',
        tabScan: 'Escanear',
        shareInstructions: 'Pide a la otra persona que escanee este QR.',
        manualHint: 'O dale este código de 8 caracteres:',
        ttl: (s) => `Caduca en ${s}s`,
        pending: 'Esperando que alguien escanee…',
        scanInstructions: 'Apunta la cámara al QR de la otra persona.',
        starting: 'Iniciando cámara…',
        cameraError: 'No se pudo acceder a la cámara. Verifica permisos.',
        retry: 'Reintentar',
        successTitle: '¡Contacto añadido!',
        successDesc: (name) => `${name} y tú quedáis como contactos mutuos.`,
        done: 'Hecho',
        cancel: 'Cancelar',
        tokenExpired: 'Código caducado. Genera uno nuevo.',
        scanFailed: 'No se pudo intercambiar. Inténtalo de nuevo.'
    },
    inglés: {
        title: 'Exchange contact',
        tabShare: 'My QR',
        tabScan: 'Scan',
        shareInstructions: 'Ask the other person to scan this QR.',
        manualHint: 'Or share this 8-character code:',
        ttl: (s) => `Expires in ${s}s`,
        pending: 'Waiting for someone to scan…',
        scanInstructions: 'Point the camera at the other person\'s QR.',
        starting: 'Starting camera…',
        cameraError: 'Could not access the camera. Check permissions.',
        retry: 'Retry',
        successTitle: 'Contact added!',
        successDesc: (name) => `${name} and you are now mutual contacts.`,
        done: 'Done',
        cancel: 'Cancel',
        tokenExpired: 'Code expired. Generate a new one.',
        scanFailed: 'Exchange failed. Try again.'
    },
    francés: {
        title: 'Échanger un contact',
        tabShare: 'Mon QR',
        tabScan: 'Scanner',
        shareInstructions: "Demande à l'autre personne de scanner ce QR.",
        manualHint: 'Ou partage ce code de 8 caractères :',
        ttl: (s) => `Expire dans ${s}s`,
        pending: "En attente d'un scan…",
        scanInstructions: 'Pointe la caméra vers le QR de l\'autre.',
        starting: 'Démarrage de la caméra…',
        cameraError: "Impossible d'accéder à la caméra. Vérifie les autorisations.",
        retry: 'Réessayer',
        successTitle: 'Contact ajouté !',
        successDesc: (name) => `${name} et toi êtes maintenant contacts mutuels.`,
        done: 'OK',
        cancel: 'Annuler',
        tokenExpired: 'Code expiré. Génère-en un nouveau.',
        scanFailed: 'Échange impossible. Réessaie.'
    },
    alemán: {
        title: 'Kontakt austauschen',
        tabShare: 'Mein QR',
        tabScan: 'Scannen',
        shareInstructions: 'Bitte die andere Person, diesen QR zu scannen.',
        manualHint: 'Oder teile diesen 8-stelligen Code:',
        ttl: (s) => `Läuft in ${s}s ab`,
        pending: 'Warte auf einen Scan…',
        scanInstructions: 'Richte die Kamera auf den QR der anderen Person.',
        starting: 'Kamera startet…',
        cameraError: 'Kamerazugriff nicht möglich. Berechtigungen prüfen.',
        retry: 'Erneut versuchen',
        successTitle: 'Kontakt hinzugefügt!',
        successDesc: (name) => `${name} und du seid jetzt gegenseitige Kontakte.`,
        done: 'Fertig',
        cancel: 'Abbrechen',
        tokenExpired: 'Code abgelaufen. Erstelle einen neuen.',
        scanFailed: 'Austausch fehlgeschlagen. Erneut versuchen.'
    },
    portugués: {
        title: 'Trocar contacto',
        tabShare: 'Meu QR',
        tabScan: 'Digitalizar',
        shareInstructions: 'Pede à outra pessoa que digitalize este QR.',
        manualHint: 'Ou partilha este código de 8 caracteres:',
        ttl: (s) => `Expira em ${s}s`,
        pending: 'À espera de uma digitalização…',
        scanInstructions: 'Aponta a câmara ao QR da outra pessoa.',
        starting: 'A iniciar câmara…',
        cameraError: 'Não foi possível aceder à câmara. Verifica permissões.',
        retry: 'Tentar de novo',
        successTitle: 'Contacto adicionado!',
        successDesc: (name) => `${name} e tu são agora contactos mútuos.`,
        done: 'Feito',
        cancel: 'Cancelar',
        tokenExpired: 'Código expirado. Gera um novo.',
        scanFailed: 'Troca falhou. Tenta de novo.'
    },
    italiano: {
        title: 'Scambia contatto',
        tabShare: 'Mio QR',
        tabScan: 'Scansiona',
        shareInstructions: 'Chiedi all\'altra persona di scansionare questo QR.',
        manualHint: 'O condividi questo codice di 8 caratteri:',
        ttl: (s) => `Scade tra ${s}s`,
        pending: 'In attesa di una scansione…',
        scanInstructions: 'Punta la fotocamera al QR dell\'altra persona.',
        starting: 'Avvio fotocamera…',
        cameraError: 'Impossibile accedere alla fotocamera. Verifica i permessi.',
        retry: 'Riprova',
        successTitle: 'Contatto aggiunto!',
        successDesc: (name) => `${name} e tu siete ora contatti reciproci.`,
        done: 'Fatto',
        cancel: 'Annulla',
        tokenExpired: 'Codice scaduto. Generane uno nuovo.',
        scanFailed: 'Scambio fallito. Riprova.'
    },
    euskera: {
        title: 'Trukatu kontaktua',
        tabShare: 'Nire QR',
        tabScan: 'Eskaneatu',
        shareInstructions: 'Eskatu beste pertsonari QR hau eskaneatzeko.',
        manualHint: 'Edo partekatu 8 karaktereko kode hau:',
        ttl: (s) => `${s}s-tan iraungitzen da`,
        pending: 'Norbaitek eskaneatzeko zain…',
        scanInstructions: 'Apuntatu kamera bestearen QR-era.',
        starting: 'Kamera abiarazten…',
        cameraError: 'Ezin izan da kamera atzitu. Egiaztatu baimenak.',
        retry: 'Saiatu berriro',
        successTitle: 'Kontaktua gehituta!',
        successDesc: (name) => `${name} eta zu elkarrekiko kontaktuak zarete orain.`,
        done: 'Eginda',
        cancel: 'Utzi',
        tokenExpired: 'Kodea iraungia. Sortu berri bat.',
        scanFailed: 'Trukea huts egin du. Saiatu berriro.'
    }
};
function pickCexL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return CEX_I18N[u?.language] || CEX_I18N.inglés;
    } catch { return CEX_I18N.inglés; }
}

// Extrae token 8 chars de un payload escaneado o tecleado.
// Acepta varios formatos:
//   - "ABC23456"  (código manual)
//   - "kaixo://contact/ABC23456"  (formato custom scheme legacy)
//   - "https://kaixo.ai/invite/ABC23456"  (formato URL para no-users
//     que escanean con cámara nativa y abren la landing de descarga)
function parseToken(raw) {
    if (!raw) return null;
    const s = String(raw).trim().toUpperCase();
    // Si es URL https://kaixo.ai/invite/?t=ABC12345, extraemos el ?t=
    const tQuery = s.match(/[?&]T=([A-Z0-9]{8})\b/);
    if (tQuery) return tQuery[1];
    // Formatos directos: token solo, o custom scheme legacy
    const m = s.match(/(?:KAIXO:\/\/CONTACT\/)?([A-Z0-9]{8})(?:[/?#]|$)/);
    return m ? m[1] : null;
}

export default function ContactExchangeModal({ onClose }) {
    const t = pickCexL();
    const [mode, setMode] = useState('share'); // 'share' | 'scan'
    const [token, setToken] = useState(null);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [exchanged, setExchanged] = useState(null); // { id, name, avatar }
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [permissionError, setPermissionError] = useState(false);

    // Refs para modo scan
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const scanTimerRef = useRef(null);

    // ─── Modo SHARE: pedir token, generar QR, polling ───────────
    useEffect(() => {
        if (mode !== 'share' || exchanged) return;
        let cancelled = false;
        let pollTimer = null;
        let currentToken = null;

        async function start() {
            setError('');
            setBusy(true);
            try {
                const { data } = await api.post('/contact-exchange/create');
                if (cancelled) return;
                currentToken = data.token;
                setToken(data.token);
                setExpiresAt(new Date(data.expiresAt).getTime());
                // Generar QR como URL HTTPS — así si la otra persona
                // escanea con la cámara NATIVA del móvil (sin tener KAIXO),
                // se le abre kaixo.ai/invite/?t=<token> con landing de
                // descarga + el código grande. Querystring en lugar de
                // path para no requerir reglas .htaccess de rewrite.
                // Si tiene KAIXO instalada, el scanner interno extrae
                // igual el token con parseToken (acepta todos los formatos).
                const qrUrl = await QRCode.toDataURL(`https://kaixo.ai/invite/?t=${data.token}`, {
                    width: 280,
                    margin: 2,
                    color: { dark: '#000000', light: '#ffffff' }
                });
                if (cancelled) return;
                setQrDataUrl(qrUrl);

                // Polling status
                pollTimer = setInterval(async () => {
                    try {
                        const { data: st } = await api.get(`/contact-exchange/status/${currentToken}`);
                        if (cancelled) return;
                        if (st.status === 'consumed' && st.user) {
                            setExchanged(st.user);
                            clearInterval(pollTimer);
                        } else if (st.status === 'expired') {
                            setError(t.tokenExpired);
                            clearInterval(pollTimer);
                        }
                    } catch { /* errores transitorios — seguir intentando */ }
                }, POLL_INTERVAL_MS);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.response?.data?.error || 'Error');
                }
            } finally {
                if (!cancelled) setBusy(false);
            }
        }
        start();

        return () => {
            cancelled = true;
            if (pollTimer) clearInterval(pollTimer);
        };
    }, [mode, exchanged, t.tokenExpired]);

    // ─── Modo SCAN: abrir cámara y escanear cada 250ms ──────────
    useEffect(() => {
        if (mode !== 'scan' || exchanged) return;
        let cancelled = false;
        async function start() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' },
                    audio: false
                });
                if (cancelled) {
                    stream.getTracks().forEach(tr => tr.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
                setPermissionError(false);
                startScanning();
            } catch (err) {
                if (!cancelled) {
                    setPermissionError(true);
                    setError(t.cameraError);
                }
            }
        }
        start();
        return () => {
            cancelled = true;
            stopScanning();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(tr => tr.stop());
                streamRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, exchanged]);

    // ─── TTL countdown (modo share) ──────────────────────────────
    useEffect(() => {
        if (!expiresAt || exchanged) return;
        const tick = () => {
            const sec = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
            setSecondsLeft(sec);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [expiresAt, exchanged]);

    // ─── Cancelar token al desmontar (si nunca se consumió) ─────
    useEffect(() => {
        return () => {
            if (token && !exchanged) {
                api.post('/contact-exchange/cancel').catch(() => {});
            }
        };
    }, [token, exchanged]);

    function startScanning() {
        stopScanning();
        scanTimerRef.current = setInterval(scanFrame, SCAN_INTERVAL_MS);
    }
    function stopScanning() {
        if (scanTimerRef.current) {
            clearInterval(scanTimerRef.current);
            scanTimerRef.current = null;
        }
    }

    function scanFrame() {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, w, h);
        let imageData;
        try { imageData = ctx.getImageData(0, 0, w, h); } catch { return; }
        const code = jsQR(imageData.data, w, h, { inversionAttempts: 'dontInvert' });
        if (code && code.data) {
            const tk = parseToken(code.data);
            if (tk) {
                stopScanning();
                redeem(tk);
            }
        }
    }

    async function redeem(tokenStr) {
        setBusy(true);
        setError('');
        try {
            const { data } = await api.post('/contact-exchange/redeem', { token: tokenStr });
            if (data?.user) {
                setExchanged(data.user);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || t.scanFailed;
            setError(msg);
            // Reanudar escaneo si la cámara sigue activa
            setTimeout(() => {
                if (!streamRef.current) return;
                setError('');
                startScanning();
            }, 2200);
        } finally {
            setBusy(false);
        }
    }

    // ─── Render ──────────────────────────────────────────────────
    return ReactDOM.createPortal(
        <div className="cex-overlay">
            <div className="cex-header">
                <button className="cex-close" onClick={onClose} aria-label={t.cancel}>
                    <Icons.Close size={22} color="white" />
                </button>
                <h2>{t.title}</h2>
            </div>

            {!exchanged && (
                <div className="cex-tabs">
                    <button
                        className={`cex-tab ${mode === 'share' ? 'active' : ''}`}
                        onClick={() => { setMode('share'); setError(''); }}
                    >{t.tabShare}</button>
                    <button
                        className={`cex-tab ${mode === 'scan' ? 'active' : ''}`}
                        onClick={() => { setMode('scan'); setError(''); }}
                    >{t.tabScan}</button>
                </div>
            )}

            {/* SHARE */}
            {mode === 'share' && !exchanged && (
                <div className="cex-share">
                    <div className="cex-qr-card">
                        {qrDataUrl
                            ? <img src={qrDataUrl} alt="QR" />
                            : <div className="cex-qr-loading">…</div>}
                    </div>
                    {token && <div className="cex-token">{token}</div>}
                    <p className="cex-instructions">{t.shareInstructions}</p>
                    {token && <p className="cex-manual-hint">{t.manualHint}</p>}
                    {expiresAt && !exchanged && (
                        <p className="cex-ttl">{t.ttl(secondsLeft)}</p>
                    )}
                    <p className="cex-status">{t.pending}</p>
                    {error && <p className="cex-error">{error}</p>}
                </div>
            )}

            {/* SCAN */}
            {mode === 'scan' && !exchanged && (
                <div className="cex-scan">
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        autoPlay
                        className="cex-video"
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                    {!permissionError && <div className="cex-frame" />}
                    <p className="cex-scan-instructions">
                        {permissionError ? t.cameraError : t.scanInstructions}
                    </p>
                    {error && <p className="cex-error">{error}</p>}
                </div>
            )}

            {/* RESULT */}
            {exchanged && (
                <div className="cex-result">
                    <div className="cex-result-icon">✓</div>
                    <div className="cex-avatar">
                        {exchanged.avatar
                            ? <img src={exchanged.avatar} alt={exchanged.name} />
                            : <span>{(exchanged.name || '?').charAt(0).toUpperCase()}</span>}
                    </div>
                    <h3>{t.successTitle}</h3>
                    <p>{t.successDesc(exchanged.name)}</p>
                    <button className="cex-btn-done" onClick={onClose}>{t.done}</button>
                </div>
            )}

            {busy && !exchanged && <div className="cex-busy" aria-hidden="true" />}
        </div>,
        document.body
    );
}
